import type {
  MemberHeaderVisibleMenuItems,
  MemberOverlayName,
  MemberProfile
} from '@library/types/members';
import { BackNavigationIcon } from '@bo-components/navigation/BackNavigationIcon';
import { KINDBRIDGE_NOT_ENROLLED } from '@bo-constants/member/memberCopy';
import { MemberHeaderActionButtons } from '@bo-components/members';
import { Tooltip } from '@library/components/tooltip/Tooltip';
import { classNames } from '@library/utils';
import { memberFeatureFlags } from '@bo-utils/members/members';
import { selectMemberActionsAccessPermissions } from '@bo-selectors/members/memberActionPermissions';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

interface MemberHeaderInfoBlockProps {
  dataTestId: string;
  label: string;
  tooltipValue?: Array<string> | string;
  value?: string | Array<string> | number | null;
  valueClassName?: string;
}

export interface MemberHeaderProps {
  memberProfile: MemberProfile;
  onShowModal: (value: MemberOverlayName) => void;
}

export const MemberHeader = ({
  memberProfile,
  onShowModal
}: MemberHeaderProps) => {
  const { generalInfo, header: headerInfo, memberOverlayInfo } = memberProfile;
  const { internalVerification, kindbridgeEnrollment } = generalInfo;
  const { isRulesetRevampActive } = memberFeatureFlags;
  const { isDeactivated } = memberOverlayInfo;

  const {
    credit: { label: creditLabel, value: creditValue },
    email,
    fullName,
    promo: { label: promoLabel, value: promoValue },
    responsibleGaming: { value: responsibleGamingValue },
    rpu: { label: rpuLabel, value: rpuValue },
    rulesets: {
      label: rulesetsLabel,
      tooltip: rulesetsTooltip,
      value: rulesetsValue
    },
    state: { label: stateLabel, value: stateValue },
    vip: { value: vipValue }
  } = headerInfo;

  const {
    hasAccessMemberCreateAdjustment,
    hasAccessMemberDeactivateUser,
    hasAccessMemberEditAccountInfo,
    hasAccessMemberEnrollKindbridge,
    hasAccessMemberManageGroups,
    hasAccessMemberRulesets,
    hasAccessMemberManageTinCollection,
    hasAccessMemberManageVipStatus,
    hasAccessMemberManuallyPassUser,
    hasAccessMemberResetPassword
  } = useSelector(selectMemberActionsAccessPermissions);

  const visibleMenuItems: MemberHeaderVisibleMenuItems = {
    createAdjustment: hasAccessMemberCreateAdjustment,
    editAccountInformation: hasAccessMemberEditAccountInfo,
    kindbridgeEnrollment:
      hasAccessMemberEnrollKindbridge &&
      !!kindbridgeEnrollment?.value?.includes(KINDBRIDGE_NOT_ENROLLED),
    manageLegacyRuleset: hasAccessMemberRulesets && !isRulesetRevampActive,
    manageMemberGroups: hasAccessMemberManageGroups && isRulesetRevampActive,
    manageRuleset: hasAccessMemberRulesets,
    manageTinCollection: hasAccessMemberManageTinCollection,
    manageVipStatus: hasAccessMemberManageVipStatus,
    manuallyPassMember:
      hasAccessMemberManuallyPassUser &&
      !internalVerification?.value?.includes('passed'),
    resetPassword: hasAccessMemberResetPassword,
    updateMemberActivation: hasAccessMemberDeactivateUser
  };

  const InfoBlock = useMemo(() => {
    return ({
      dataTestId,
      label,
      tooltipValue = undefined,
      value = '',
      valueClassName = ''
    }: MemberHeaderInfoBlockProps) => (
      <Tooltip
        text={tooltipValue}
        position="below"
        className="capitalize leading-3"
      >
        <div className="bg-soFresh-120 flex flex-col gap-1 rounded-lg px-4 py-2.5">
          <p className="text-soClean-180 leading-none">{label}</p>

          <p
            className={classNames(
              'text-soClean-100 min-h-3.5 font-semibold leading-none',
              valueClassName
            )}
            data-testid={dataTestId}
          >
            {value}
          </p>
        </div>
      </Tooltip>
    );
  }, []);

  return (
    <div className="border-soFresh-140 flex h-16 w-full items-center gap-4 border-b px-6 pb-3">
      <div className="flex items-center gap-2">
        <BackNavigationIcon path="/members" />
        <div>
          <h2 className="text-soClean-180">Members</h2>
          <div className="flex gap-2" data-testid="header-member-name">
            <span className="text-soClean-100">{fullName?.value}</span>
            {vipValue && (
              <span
                className="bg-spottieOttie text-soFresh-100 rounded px-2 py-1 text-sm font-medium"
                data-testid="header-vip-status"
              >
                VIP
              </span>
            )}
            {responsibleGamingValue && (
              <span
                className="bg-msJackson text-soFresh-100 rounded px-2 py-1 text-sm font-medium"
                data-testid="header-rg-status"
              >
                RG
              </span>
            )}
            <span
              className="text-soClean-180"
              data-testid="header-email"
            >{`• ${email?.value}`}</span>
          </div>
        </div>
      </div>
      <div className="ml-auto flex items-center justify-center gap-4 text-center text-sm">
        <InfoBlock
          dataTestId="header-credit"
          label={creditLabel}
          value={creditValue}
        />
        <InfoBlock
          dataTestId="header-promo"
          label={promoLabel}
          value={promoValue}
        />
        <InfoBlock
          dataTestId="header-rpu"
          label={rpuLabel}
          value={`$${rpuValue}`}
        />
        <InfoBlock
          dataTestId="header-state"
          label={stateLabel}
          value={stateValue}
        />
        <InfoBlock
          dataTestId="header-member-groups"
          label={rulesetsLabel}
          tooltipValue={rulesetsTooltip}
          value={rulesetsValue}
          valueClassName={isDeactivated ? '!text-roses' : ''}
        />
      </div>
      <MemberHeaderActionButtons
        isDeactivated={isDeactivated}
        onShowModal={onShowModal}
        visibleMenuItems={visibleMenuItems}
      />
    </div>
  );
};
